import { ElementRef, Injectable, ViewChild } from '@angular/core';
import jp from 'jsonpath';
import { ConfigField, ConfigQuestion, ConfigSection, ConfigSubsection } from '../model/ddq-config';
import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { QuestionnaireService } from './questionnaire.service';
import { requireCheckboxesToBeCheckedValidator } from '../ddq/active-section/require-checkbox-validator';
import { requirePercentControlValidator } from '../ddq/active-section/percent-control-validator';
import { requirePercentFormArrayValidator } from '../ddq/active-section/percent-validator-formarray';
import { enableDisableValidator } from '../ddq/active-section/enable-disable-validator';
import { enableDisableSelectValidator } from '../ddq/active-section/enable-disable-select-validator';
import { enableDisableCheckboxValidator } from '../ddq/active-section/enable-disable-checkbox-validator';
import { disableQuestionValidator } from '../ddq/active-section/disable-question-validator';

import { BehaviorSubject, startWith } from 'rxjs';
import { CurrentUserService } from 'src/app/services/current-user.service';

const username = 'jane.doe';

function requireWhenSigned(signCtrl: UntypedFormControl): ValidatorFn {
  return (ctrl): ValidationErrors | null => {
    const required = signCtrl.value === true;
    const hasText = (ctrl.value ?? '').toString().trim().length > 0;
    return required && !hasText ? { requiredWhenSigned: true } : null;
  };
}
@Injectable({
  providedIn: 'root',
})
export class DdqService {
  formData;
  formAnswers;
  formIsReadOnly = true;
  isMDAform: boolean;
  private _username = '';
  formArrays = [];
  @ViewChild('onNotifyModal') onNotifyModal!: ElementRef;
  private snapshotData = new BehaviorSubject(null);
  currentSnapshotData = this.snapshotData.asObservable();
  constructor(private questionnaireService: QuestionnaireService, private currentUserService: CurrentUserService) {
    this.currentUserService.currentUser
      .pipe(/* e.g., distinctUntilChanged(...) */)
      .subscribe((u) => (this._username = typeof u === 'string' ? u : u?.email ?? ''));
  }

  updateSnapshotData(data: any) {
    this.snapshotData.next(data);
  }

  allData() {
    let x = this.checkAnswersforFormArray(this.formAnswers);
    this.formArrays = x.filter((v, i, a) => a.findIndex((v2) => v2.key === v.key) === i);
    return this.formData;
  }

  sectionOne() {
    return this.formData[0] || null;
  }

  sectionOnePageOne() {
    return this.formData[0].pages[0] || null;
  }

  getQuestion(id) {
    const questions = [].concat(...jp.query(this.formData, '$..questions.*'));
    return questions.filter((question) => question.id == id);
  }

  getFields(id) {
    const fields = [].concat(...jp.query(this.formData, '$..fields.*'));
    return fields.filter((field) => {
      return field.id == id;
    });
  }

  getOptions(id) {
    const options = [].concat(...jp.query(this.formData, '$..options.*'));
    return options.filter((option) => {
      return option.id == id;
    });
  }

  addSection(section: ConfigSection, isMDA: boolean): UntypedFormGroup {
    const sectionGroup = new UntypedFormGroup({});
    section.subsections.forEach((subsection) => {
      sectionGroup.addControl(subsection.id, this.addSubsection(subsection, isMDA));
    });
    const sectionControlsArr = Object.values(sectionGroup.controls);
    const sectionStatusArr = sectionControlsArr.map((section) => section.touched);
    const dirtySectionStatus = sectionStatusArr.some((isTrue) => isTrue === true);
    if (dirtySectionStatus) {
      sectionGroup.markAsDirty();
      sectionGroup.markAsTouched();
    }

    return sectionGroup;
  }

  addSubsection(subsection: ConfigSubsection, isMDA: boolean): UntypedFormGroup {
    const subsectionGroup = new UntypedFormGroup({});

    subsection.questions.forEach((question) => {
      // existing controls
      subsectionGroup.addControl('DQ-' + question.id, this.returnControl(question));
      subsectionGroup.addControl('AN-' + question.id, this.returnControlWithoutValidatorOnBlur());
      subsectionGroup.addControl('ANC-' + question.id, this.returnControlWithoutValidator());

      subsectionGroup.addControl('EDIT-' + question.id, this.returnControlWithoutValidator());

      // keys
      const signKey = 'SIGN-' + question.id;
      const signedKey = 'SIGNEDBY-' + question.id;
      const editKey = 'EDIT-' + question.id;

      // create sign + signedBy
      subsectionGroup.addControl(signKey, new UntypedFormControl(false)); // unchecked by default
      subsectionGroup.addControl(signedKey, this.returnControlWithoutValidator()); // free text

      if (isMDA) {
        // wire them
        const signCtrl = subsectionGroup.get(signKey) as UntypedFormControl;
        const signedCtrl = subsectionGroup.get(signedKey) as UntypedFormControl;
        const editCtrl = subsectionGroup.get(editKey) as UntypedFormControl;

        signCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

        // B) SIGNEDBY required only when SIGN is checked
        signedCtrl.setValidators((ctrl) => {
          const required = signCtrl.value === true;
          const hasText = (ctrl.value ?? '').toString().trim().length > 0;
          return required && !hasText ? { requiredWhenSigned: true } : null;
        });
        signedCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });

        // 🧩 get username from CurrentUserService (handles string OR object {username})
        const getUsername = () => this._username;
        // C) auto-stamp SIGNEDBY and flip EDIT=false when SIGN toggles
        const stamp = () =>
          `${getUsername()} — ${new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date()
          )}`;

        signCtrl.valueChanges.pipe(startWith(signCtrl.value)).subscribe((checked: boolean) => {
          signedCtrl.patchValue(checked ? stamp() : '', { emitEvent: false, onlySelf: true });
          if (checked) editCtrl.patchValue(false, { emitEvent: false, onlySelf: true });
          signedCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        });

        editCtrl.valueChanges.pipe(startWith(editCtrl.value)).subscribe((checked: boolean) => {
          if (checked) {
            signCtrl.patchValue(false, { emitEvent: false, onlySelf: true });
            signedCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          }
        });
      }

      // payload group
      subsectionGroup.addControl(question.id, this.addQuestion(question, isMDA));
    });

    // keep your touched/dirty logic
    const quControlsArr = Object.values(subsectionGroup.controls);
    const quStatusArr = quControlsArr.map((qu) => qu.touched);
    const dirtyStatus = quStatusArr.some((isTrue) => isTrue === true);
    if (dirtyStatus) {
      subsectionGroup.markAsDirty();
      subsectionGroup.markAsTouched();
    }
    return subsectionGroup;
  }

  returnControl(question) {
    let control: UntypedFormControl;
    control = new UntypedFormControl('', {
      validators: [disableQuestionValidator(question, this.isMDAform)],
      updateOn: 'change',
    });
    return control;
  }

  returnControlWithoutValidator() {
    let control: UntypedFormControl;
    control = new UntypedFormControl('', {
      validators: [],
      updateOn: 'change',
    });
    return control;
  }

  // returnControlWithEnableEditValidator(question) {
  //   let control: UntypedFormControl;
  //   control = new UntypedFormControl('', {
  //     validators:[enableQuestionValidator(question)],
  //     updateOn: 'change',
  //   });
  // return  control;
  // }

  returnControlWithoutValidatorOnBlur() {
    let control: UntypedFormControl;
    control = new UntypedFormControl('', {
      validators: [],
      updateOn: 'blur',
    });
    return control;
  }

  // addQuestionWrapper(question: ConfigQuestion): FormGroup {
  //   const questionWrapper = new FormGroup({});
  //  // Add disable control
  //  let control: FormControl;
  //  control = new FormControl('', {
  //    validators:[],
  //    updateOn: 'change',
  //  });
  //  questionWrapper.addControl('XXX-' + question.id, control);

  // return questionWrapper;
  // }

  addQuestion(question: ConfigQuestion, isMDA: boolean): UntypedFormGroup {
    const questionGroup = new UntypedFormGroup({}, this.addValidation(question));

    if (question.dynamic == true) {
      let control: UntypedFormControl;
      control = new UntypedFormControl('', {
        validators: [enableDisableCheckboxValidator(question.dynamicDisableObject?.conditional)],
        updateOn: 'change',
      });
      questionGroup.addControl(question.dynamicDisableObject.id, control);

      const test = this.formArrays.find((obj) => obj.key === question.dynamicDisableObject.arrayId);
      // console.log('T', test);
      if (test) {
        questionGroup.addControl(question.dynamicDisableObject.arrayId, this.addDynamicQuestion(question, test, isMDA));
      }
    } else {
      question.fields.forEach((field: ConfigField) => {
        //const fieldValue = field.value ? field.value : '';
        const fieldValue = '';
        if (Array.isArray(field)) {
          field.forEach((subField) => {
            questionGroup.addControl(subField.id, this.addField(subField, question, isMDA));
          });
        } else questionGroup.addControl(field.id, this.addField(field, question, isMDA));

        if (fieldValue !== '') {
          questionGroup.markAsDirty();
          questionGroup.markAsTouched();
        }
      });
    }

    return questionGroup;
  }

  addValidation(question) {
    if (question.validate != undefined && question.validate.groupArr != undefined) {
      return requireCheckboxesToBeCheckedValidator(question?.validate?.groupArr);
    } else {
      return;
    }
  }

  addDynamicQuestion(question: ConfigQuestion, test, isMDA: boolean = false): UntypedFormArray {
    const fieldArray = new UntypedFormArray([], requirePercentFormArrayValidator(question?.validate?.percent));
    const fieldGroups = question.fields;

    let markFormArrayAsDirty = false;
    // console.log(test);
    for (let i = 0; i < test['length']; i++) {
      fieldGroups.forEach((fields: ConfigField[]) => {
        let markGroupAsDirty = false;
        let fieldGroup = new UntypedFormGroup({}, this.addValidation(question));
        fields.forEach((field: ConfigField) => {
          if (field.value || question.disabled) {
            markFormArrayAsDirty = true;
            markGroupAsDirty = true;
          }
          field.disabled = question.disabled;
          fieldGroup.addControl(field.id, this.addField(field, question, isMDA));
        });

        if (markGroupAsDirty) {
          fieldGroup.markAsDirty();
          fieldGroup.markAsTouched();
          fieldGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        }

        fieldArray.push(fieldGroup);
      });
    }

    if (markFormArrayAsDirty) {
      fieldArray.markAsDirty();
      fieldArray.markAsTouched();
      fieldArray.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    return fieldArray;
  }

  checkAnswersforFormArray(answers) {
    let array = [];
    const iterate = (obj) => {
      Object.keys(obj).forEach((key) => {
        if (Array.isArray(obj[key])) {
          let answersArray = [];
          obj[key].forEach((element) => {
            if (typeof element === 'object') {
              answersArray.push(element);
              let item = {
                key: key,
                length: obj[key].length,
                answers: answersArray,
              };
              array.push(item);
            }
          });
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          iterate(obj[key]);
        }
      });
    };
    iterate(answers);
    return array;
  }

  addDynamicFields(question, isMDA: boolean = false) {
    let markGroupAsDirty = false;
    let fields = question.fields;
    let fieldGroup = new UntypedFormGroup({}, this.addValidation(question));
    fields.forEach((field: ConfigField) => {
      if (field.value || question.disabled) {
        //  markFormArrayAsDirty = true;
        markGroupAsDirty = true;
      }
      field.disabled = question.disabled;
      fieldGroup.addControl(field.id, this.addField(field, question, isMDA));
    });

    if (markGroupAsDirty) {
      fieldGroup.markAsDirty();
      fieldGroup.markAsTouched();
      fieldGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    return fieldGroup;
  }

  addField(field: ConfigField, question: ConfigQuestion, isMDA: boolean): UntypedFormControl {
    //const fieldValue = field.value ? field.value : '';
    const fieldValue = '';
    const disabled = field.disabled ? field.disabled : '';
    let control: UntypedFormControl;

    if (field.validate) {
      if (field.validate.required && (field.type === 'radio-inline' || field.type === 'radio-list')) {
        control = new UntypedFormControl(fieldValue, {
          validators: [Validators.required, enableDisableValidator(field, this.getFields(field?.id))],
          updateOn: 'change',
        });
      } else if (field.validate.required === true && field.type === 'dropdown') {
        control = new UntypedFormControl(fieldValue, {
          validators: [Validators.required, enableDisableSelectValidator(field?.conditional)],
          updateOn: 'change',
        });
      } else if (field.validate.required === true && !isMDA) {
        control = new UntypedFormControl(fieldValue, {
          validators: Validators.required,
          updateOn: 'blur',
        });
      } else if (field.validate.required === true && isMDA) {
        control = new UntypedFormControl(fieldValue, {
          validators: [],
          updateOn: 'blur',
        });
      } else if (field.validate.requiredCheckbox === true) {
        control = new UntypedFormControl(fieldValue, {
          //validators: Validators.requiredTrue,
          updateOn: 'change',
        });
      } else if (field.validate.email === true) {
        control = new UntypedFormControl(fieldValue, {
          validators: Validators.email,
          updateOn: 'blur',
        });
      } else if (field.validate.requiredEmail === true) {
        control = new UntypedFormControl(fieldValue, {
          validators: [Validators.required, Validators.email],
          updateOn: 'blur',
        });
      } else if (field.validate.requiredPhone === true) {
        control = new UntypedFormControl(fieldValue, {
          validators: [Validators.required, Validators.pattern('^(0|\\+)\\d{8,15}$')],
          updateOn: 'blur',
        });
      } else if (field.validate.percent !== undefined) {
        control = new UntypedFormControl(fieldValue, {
          validators: [Validators.required, requirePercentControlValidator(field?.validate?.percent)],
          updateOn: 'blur',
        });
      }
    } else {
      if (field.type == 'radio-inline' || field.type == 'radio-list') {
        control = new UntypedFormControl(fieldValue, {
          validators: [enableDisableValidator(field, this.getFields(field?.id))],
          updateOn: 'change',
        });
      } else if (field.type == 'checkbox') {
        control = new UntypedFormControl(fieldValue, {
          validators: [enableDisableCheckboxValidator(field?.conditional)],
          updateOn: 'change',
        });
      } else {
        control = new UntypedFormControl(fieldValue, { updateOn: 'blur' });
      }
    }

    if (fieldValue) {
      control.markAsDirty();
      control.markAsTouched();
    }

    if (disabled) {
      control.disable();
    }
    // removed to ddd component to save whole answers object
    // control.valueChanges.subscribe((val) => {
    //   if (!this.questionnaireService.disableSaving) {
    //     this.questionnaireService.saveDDQ(val, field, question, this.formData, control);
    //   }
    // });

    return control;
  }
}
